import { useState, useEffect, useRef, useCallback } from "react";

/**
 * usePopoverBehavior({ minWidth })
 *
 * Encapsulates the open/close state, fixed-position style calculation, and
 * click-outside / scroll / resize dismissal shared by CalendarPopup,
 * TimePopup, and DateTimePopup.
 *
 * Returns:
 *   open          bool         — whether the popover is visible
 *   setOpen       fn           — direct setter (for manual close after selection)
 *   openPopover   fn           — call from trigger onClick; computes position & opens
 *   close         fn           — stable close callback
 *   popoverStyle  object       — pass as `style` on the portal element
 *   triggerRef    React ref    — attach to the trigger button
 *   popoverRef    React ref    — attach to the portal container
 *
 * Options:
 *   minWidth (number | null) — if provided, `Math.max(triggerWidth, minWidth)` is
 *                              added to the style. Omit or pass null to let the
 *                              popover size itself (e.g. DateTimePopup).
 */
export function usePopoverBehavior({ minWidth = null } = {}) {
  const [open, setOpen]               = useState(false);
  const [popoverStyle, setPopoverStyle] = useState({});
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);

  function openPopover() {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      const style = {
        position: "fixed",
        top:      rect.bottom + 6,
        left:     rect.left,
        zIndex:   50,
      };
      if (minWidth != null) {
        style.width = Math.max(rect.width, minWidth);
      }
      setPopoverStyle(style);
    }
    setOpen(true);
  }

  useEffect(() => {
    if (!open) return;

    function onMouseDown(e) {
      if (
        !triggerRef.current?.contains(e.target) &&
        !popoverRef.current?.contains(e.target)
      ) {
        close();
      }
    }
    function onScroll(e) {
      if (popoverRef.current?.contains(e.target)) return;
      close();
    }

    document.addEventListener("mousedown", onMouseDown);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", close);
    };
  }, [open, close]);

  return { open, setOpen, openPopover, close, popoverStyle, triggerRef, popoverRef };
}
